import { ConsentOrdersPathName, FormNavigation, FormProgress, InitialDetails } from "@fla/components";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { useQuery } from "hooks/useQuery";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { selectConsentOrdersBase, selectInitialDetailsStep } from "../consentOrdersSelectors";
import { consentOrdersActions } from "../consentOrdersSlice";
import { initialState } from "./helper";
import { formSteps } from "./steps";

function renameKeys(obj: any) {
  const keyValues = Object.keys(obj).map((key, idx) => {
    return { [idx + 1]: obj[key] };
  });
  return Object.assign({}, ...keyValues);
}

function InitialDetailsConsent() {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const steps = useSelector(selectInitialDetailsStep);
  const consentOrders = useSelector(selectConsentOrdersBase);

  const { state } = useLocation();
  const { saveInitialDetails, isSubmitting } = useConsentOrders();

  const [formState, setFormState] = useState<InitialDetails>(
    useMemo(() => {
      if (state?.doc?.data) return state.doc.data;
      else if (consentOrders?.initialDetails?.updatedAt) {
        const copy = { ...consentOrders.initialDetails };
        // @ts-ignore
        delete copy.isCompleted;
        // @ts-ignore
        delete copy.currentStep;
        delete copy.createdAt;
        delete copy.updatedAt;

        return copy;
      } else {
        return initialState;
      }
    }, [state?.doc?.data, consentOrders])
  );

  const filteredByKey = Object.fromEntries(Object.entries(formSteps).filter(([key]) => key !== "5"));

  const _formSteps = formState?.children?.if_children?.toLowerCase() === "no" ? renameKeys(filteredByKey) : formSteps;

  const CurrentTab = _formSteps[steps.currentStep];

  const isLastStep = () => steps.currentStep === Object.keys(_formSteps).length;
  const isFirstStep = () => steps.currentStep === 1;

  const isLast = isLastStep();
  const isFirst = isFirstStep();

  const onSubmit = async (formData: InitialDetails) => {
    setFormState(prevState => ({ ...prevState, ...formData }));

    const isDBCallCompleted = await saveInitialDetails({
      currentStep:
        consentOrders?.initialDetails?.currentStep > steps.currentStep
          ? consentOrders?.initialDetails?.currentStep
          : isLast
          ? steps.currentStep
          : steps.currentStep + 1,
      data: formData,
      isLast,
      isFirst
    });

    if (!isDBCallCompleted) return;

    if (isLast) {
      dispatch(
        consentOrdersActions.setInitialDetailsStep({
          passedSteps: [
            ...new Set([...steps.passedSteps, formState?.children?.if_children?.toLowerCase() === "no" ? 14 : 15])
          ]
        })
      );
      dispatch(
        consentOrdersActions.setAssetsDebts({
          currentStep: 1
        })
      );
      navigateTo(ConsentOrdersPathName.RealEstate);
      toast("Initial details have been saved successfully.", {
        type: "success"
      });
    } else {
      const passedStep = Array(steps.currentStep)
        .fill({})
        .map((_, idx) => idx + 1);

      dispatch(
        consentOrdersActions.setInitialDetailsStep({
          passedSteps: [...new Set([...steps.passedSteps, ...passedStep])],
          currentStep: steps.currentStep + 1
        })
      );

      window.scrollTo(0, 0);
    }
  };

  const handleBackClick = () => {
    if (!isFirst) {
      dispatch(
        consentOrdersActions.setInitialDetailsStep({
          currentStep: steps.currentStep - 1
        })
      );
    } else navigateTo(ConsentOrdersPathName.Welcome);
  };

  useEffect(() => {
    const section = document.querySelector("#formContainer");
    if (section && typeof section.scrollIntoView === "function") {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [steps.currentStep]);

  useEffect(() => {
    if (consentOrders?.initialDetails?.currentStep) {
      dispatch(
        consentOrdersActions.setInitialDetailsStep({
          currentStep: consentOrders?.initialDetails?.currentStep,
          passedSteps: Array(
            consentOrders?.initialDetails?.isCompleted
              ? consentOrders?.initialDetails?.currentStep
              : consentOrders?.initialDetails?.currentStep - 1
          )
            .fill({})
            .map((_, idx) => idx + 1)
        })
      );
    }
  }, []);

  const incomingStep = useQuery().get("step");
  useEffect(() => {
    if (incomingStep) {
      dispatch(
        consentOrdersActions.setInitialDetailsStep({
          currentStep: Number(incomingStep)
        })
      );
    }
  }, [incomingStep]);

  return (
    <section className="col-span-12 lg:col-span-10 relative md:py-10 md:px-4 lg:px-0" id="formContainer">
      <FormProgress
        fill={steps?.currentStep}
        total={Object.keys(formSteps).length}
        classes="max-w-2xl my-10 mx-5 md:mx-auto"
      />
      <div className="bg-white max-w-2xl py-5 px-5 md:p-10 mx-auto md:rounded-lg md:shadow-md">
        <CurrentTab formId="initial-details" store={formState} onSubmit={onSubmit} currentStep={steps?.currentStep} />
        <FormNavigation
          isFirst={false}
          form="initial-details"
          isSubmitting={isSubmitting}
          handleBackClick={handleBackClick}
          isLast={isLast}
          lastButtonText="Next"
          classes="mt-10"
        />
      </div>
    </section>
  );
}

export default withConsentOrder(InitialDetailsConsent);
